import { useCallback, useMemo, useState } from "react";
import { List, useGetList } from "react-admin";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

import { EmployeeServicePath, STAFF_REQUEST } from "utils/constants";
import { StaffRequestStatus, StaffRequestType } from "utils/choices";
import { employeeRequests } from "api/employeeApi";
import { administrationRequests } from "api/administrationApi";
import { accountRequests } from "api/accountApi";
import { settings } from "app";
import StaffRequestsTableBody from "../components/StaffRequestsTableBody";
import { validateStart, validateEnd } from "components/Leaves/Requests/utils/validations";

const getOptionLabel = option => (option?.name ? String(option.name) : "");
const isOptionEqualToValue = (option, value) => option?.id === value?.id;

const RequestsHistoryList = ({ title }) => {
  const [filters, setFilters] = useState(null);
  const [employeeIds, setEmployeeIds] = useState([]);
  const [createdByUserId, setCreatedByUserId] = useState(null);
  const [type, setType] = useState(null);
  const [status, setStatus] = useState(null);
  const [structuralUnit, setStructuralUnit] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  const { data: employeeData } = useGetList(
    employeeRequests.getEmployeesSelect(),
    { pagination: { page: 1, perPage: 1000 }, sort: { field: "lastName", order: "ASC" } },
    { staleTime: 5 * 60 * 1000, onError: err => console.error(err) },
  );
  const employeeChoices = useMemo(
    () => (employeeData ?? []).map(e => ({ id: e.id, name: `${e.firstName} ${e.lastName}` })),
    [employeeData],
  );

  const { data: userData } = useGetList(
    accountRequests.getUsersSelect(),
    { pagination: { page: 1, perPage: 1000 }, sort: { field: "lastName", order: "ASC" } },
    { staleTime: 5 * 60 * 1000, onError: err => console.error(err) },
  );
  const userChoices = useMemo(
    () =>
      (userData ?? []).map(u => ({
        id: u.id,
        name: u.secondName
          ? `${u.firstName} ${u.secondName} ${u.lastName}`
          : `${u.firstName} ${u.lastName}`,
      })),
    [userData],
  );

  const { data: structuralUnitData } = useGetList(
    administrationRequests.getStructuralUnitsSelect(),
    { pagination: { page: 1, perPage: 1000 }, sort: { field: "name", order: "ASC" } },
    { staleTime: 5 * 60 * 1000, onError: err => console.error(err) },
  );
  const structuralUnitChoices = useMemo(
    () => (structuralUnitData ?? []).map(u => ({ id: u.id, name: u.nameBg || u.name })),
    [structuralUnitData],
  );

  const hasAnyFilter =
    employeeIds.length > 0 ||
    [createdByUserId, type, status, structuralUnit, dateFrom, dateTo].some(v => v != null);

  const selectedEmployees = useMemo(
    () => employeeChoices.filter(o => employeeIds.includes(o.id)),
    [employeeChoices, employeeIds],
  );
  const selectedCreatedBy = useMemo(
    () => userChoices.find(o => o.id === createdByUserId) ?? null,
    [userChoices, createdByUserId],
  );

  const onEmployeeChange = useCallback((_, value) => setEmployeeIds(value.map(v => v.id)), []);
  const onCreatedByChange = useCallback((_, value) => setCreatedByUserId(value?.id ?? null), []);
  const renderEmployeeInput = useCallback(
    params => (
      <TextField
        {...params}
        label="Служители"
      />
    ),
    [],
  );
  const renderCreatedByInput = useCallback(
    params => (
      <TextField
        {...params}
        label="Създаден от"
      />
    ),
    [],
  );

  const onDownload = () => {
    setFilters({ employeeIds, createdByUserId, type, status, structuralUnit, dateFrom, dateTo });
  };

  const activeFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(filters ?? {}).filter(([, v]) =>
          Array.isArray(v) ? v.length > 0 : v != null && v !== "",
        ),
      ),
    [filters],
  );

  const onClear = () => {
    setEmployeeIds([]);
    setCreatedByUserId(null);
    setType(null);
    setStatus(null);
    setStructuralUnit(null);
    setDateFrom(null);
    setDateTo(null);
  };

  if (!filters) {
    return (
      <Box sx={settings.style.InputCardStyle}>
        <Card>
          <Typography
            variant="h5"
            sx={{ margin: "2rem 0rem 0rem", textAlign: "center" }}
          >
            История и статус на заявките
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            display="block"
            sx={{ textAlign: "center", px: 2 }}
          >
            Изберете един или повече филтри, за да видите резултатите
          </Typography>
          <CardContent sx={{ ...settings.style.CardContentStyle, width: "640px", maxWidth: "100%" }}>
            <Grid
              container
              spacing={2}
            >
              <Grid
                item
                xs={12}
                sm={12}
              >
                <FormControl fullWidth>
                  <Autocomplete
                    multiple
                    filterSelectedOptions
                    sx={{ marginTop: "-8px" }}
                    options={employeeChoices}
                    getOptionLabel={getOptionLabel}
                    noOptionsText="Няма намерени резултати"
                    value={selectedEmployees}
                    onChange={onEmployeeChange}
                    isOptionEqualToValue={isOptionEqualToValue}
                    renderInput={renderEmployeeInput}
                  />
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
              >
                <FormControl fullWidth>
                  <Autocomplete
                    sx={{ marginTop: "-8px" }}
                    options={userChoices}
                    getOptionLabel={getOptionLabel}
                    noOptionsText="Няма намерени резултати"
                    value={selectedCreatedBy}
                    onChange={onCreatedByChange}
                    isOptionEqualToValue={isOptionEqualToValue}
                    renderInput={renderCreatedByInput}
                  />
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
              >
                <FormControl fullWidth>
                  <InputLabel
                    id="approved-type-label"
                    sx={{ paddingX: "2px" }}
                  >
                    Тип
                  </InputLabel>
                  <Select
                    labelId="approved-type-label"
                    value={type ?? ""}
                    onChange={e => setType(e.target.value === "" ? null : e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Всички</em>
                    </MenuItem>
                    {StaffRequestType.map(choice => (
                      <MenuItem
                        key={choice.id}
                        value={choice.id}
                      >
                        {choice.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
              >
                <FormControl fullWidth>
                  <InputLabel
                    id="approved-status-label"
                    sx={{ paddingX: "2px" }}
                  >
                    Статус
                  </InputLabel>
                  <Select
                    labelId="approved-status-label"
                    value={status ?? ""}
                    onChange={e => setStatus(e.target.value === "" ? null : e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Всички</em>
                    </MenuItem>
                    {StaffRequestStatus.map(choice => (
                      <MenuItem
                        key={choice.id}
                        value={choice.id}
                      >
                        {choice.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
              >
                <FormControl fullWidth>
                  <InputLabel
                    id="approved-structural-unit-label"
                    sx={{ paddingX: "2px" }}
                  >
                    Факултет
                  </InputLabel>
                  <Select
                    labelId="approved-structural-unit-label"
                    value={structuralUnit ?? ""}
                    onChange={e => setStructuralUnit(e.target.value === "" ? null : e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Всички</em>
                    </MenuItem>
                    {structuralUnitChoices.map(choice => (
                      <MenuItem
                        key={choice.id}
                        value={choice.id}
                      >
                        {choice.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  type="date"
                  label="От дата"
                  InputLabelProps={{ shrink: true }}
                  value={dateFrom ?? ""}
                  onChange={e => setDateFrom(e.target.value || null)}
                  validate={validateStart(dateFrom, dateTo, false, "Началната дата трябва да е по-ранна от крайната", null, null)}
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={6}
              >
                <TextField
                  fullWidth
                  type="date"
                  label="До дата"
                  InputLabelProps={{ shrink: true }}
                  value={dateTo ?? ""}
                  onChange={e => setDateTo(e.target.value || null)}
                  validate={validateEnd(dateFrom, dateTo, false, "Крайната дата трябва да е по-късна от началната", null, null)}
                />
              </Grid>
            </Grid>

            <Grid
              container
              justifyContent="space-between"
              spacing={1}
              sx={{ mt: 2 }}
            >
              <Grid item>
                <Button
                  variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={onClear}
                  disabled={!hasAnyFilter}
                >
                  Изчисти
                </Button>
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<FileDownloadIcon />}
                  onClick={onDownload}
                  disabled={!hasAnyFilter}
                >
                  Покажи
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 5 }}>
        <Button
          variant="outlined"
          startIcon={<CloseIcon />}
          onClick={() => setFilters(null)}
        >
          Промени филтъра
        </Button>
      </Box>
      <List
        exporter={false}
        title={title}
        resource={`${EmployeeServicePath}${STAFF_REQUEST}/search`}
        filter={activeFilters}
      >
        <StaffRequestsTableBody showApproveButton={false} />
      </List>
    </>
  );
};

export default RequestsHistoryList;
